import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = [
    "modal", "nominationsContainer", "nominationCheckbox", "modelBlock",
    "selectModelSection", "selectedModelSection", "selectedModelName", "mdlUserInput",
    "searchInput", "searchResults", "newUserForm", "toggleFormButton", 
    "createUserForm", "createUserErrors", "submitButton", "codeInput"
  ]

  connect() {
    this.searchTimeout = null
    this.currentNominationId = null
    this.modal = null
    
    // Слушаем событие успешного сканирования QR-кода
    this.element.addEventListener('qr-scanner-modal:scanned', this.handleQrScanned.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('qr-scanner-modal:scanned', this.handleQrScanned.bind(this))
  }

  // Открытие QR сканера для формы создания модели
  openQrScanner(event) {
    event.preventDefault()
    
    // Получаем контроллер qr-scanner-modal
    const qrScannerController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'qr-scanner-modal'
    )
    
    if (qrScannerController) {
      qrScannerController.open({
        targetInput: this.hasCodeInputTarget ? this.codeInputTarget : null,
        callback: null
      })
    }
  }

  // Открытие QR сканера для выбора модели из поиска
  openQrScannerForUser(event) {
    event.preventDefault()
    
    const userId = event.currentTarget.dataset.userId
    const userName = event.currentTarget.dataset.userName
    
    // Получаем контроллер qr-scanner-modal
    const qrScannerController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'qr-scanner-modal'
    )
    
    if (qrScannerController) {
      qrScannerController.open({
        targetInput: null,
        userId: userId,
        userName: userName,
        callback: (result) => {
          this.handleQrScannedForUser(result)
        }
      })
    }
  }

  // Обработка QR-кода при сканировании для существующего пользователя
  handleQrScannedForUser(result) {
    const { code, userId, userName } = result
    
    // Создаём запись competition_user с кодом и выбираем модель
    this.createCompetitionUserWithCode(userId, userName, code)
  }

  // Обработка события сканирования (для формы создания - код устанавливается автоматически через targetInput)
  handleQrScanned(event) {
    const { code, userId, userName } = event.detail
    
    // Если сканирование было для существующего пользователя
    if (userId && userName) {
      this.handleQrScannedForUser({ code, userId, userName })
    }
    // Если сканирование было для формы создания - код уже установлен через targetInput
  }

  // Создание competition_user с кодом и выбор модели
  createCompetitionUserWithCode(userId, userName, code) {
    const url = new URL('/admin/competitions/' + this.getCompetitionId() + '/masters/get_model_section', window.location.origin)
    url.searchParams.append('user_id', userId)
    url.searchParams.append('nomination_id', this.currentNominationId)
    url.searchParams.append('code', code)
    
    fetch(url, {
      headers: {
        'Accept': 'text/html, application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('application/json')) {
        return response.json().then(data => {
          if (!data.success && data.error) {
            this.showNotification(data.error, 'error')
          }
          return null
        })
      }
      
      return response.text()
    })
    .then(html => {
      if (html === null) return
      
      // Обновляем интерфейс
      this.updateSelectedModelWithHtml(this.currentNominationId, userId, html)
      this.closeModal()
    })
    .catch(error => {
      console.error('Error creating competition user with code:', error)
      this.showNotification('Ошибка при сохранении', 'error')
    })
  }

  // Обновление выбранной модели с уже готовым HTML
  updateSelectedModelWithHtml(nominationId, userId, html) {
    const container = this.element.querySelector(`[data-nomination-id="${nominationId}"]`)
    const selectSection = container.querySelector('[data-model-selection-target="selectModelSection"]')
    const selectedSection = container.querySelector('[data-model-selection-target="selectedModelSection"]')
    const hiddenInput = container.querySelector('[data-model-selection-target="mdlUserInput"]')
    
    hiddenInput.value = userId
    selectedSection.innerHTML = html
    selectSection.style.display = 'none'
    selectedSection.style.display = 'block'
  }

  toggleModelButton(event) {
    const checkbox = event.currentTarget
    const nominationId = this.getNominationId(checkbox)
    const modelBlock = this.findModelBlock(nominationId)
    
    if (checkbox.checked) {
      modelBlock.style.display = 'block'
    } else {
      modelBlock.style.display = 'none'
      // Очищаем выбранную модель при снятии галочки
      this.clearModelForNomination(nominationId)
    }
  }

  openModal(event) {
    this.currentNominationId = event.currentTarget.dataset.nominationId
    
    // Инициализируем модальное окно Bootstrap
    if (!this.modal) {
      this.modal = new Modal(this.modalTarget)
    }
    
    // Очищаем форму поиска
    this.resetModal()
    
    this.modal.show()
  }

  closeModal() {
    if (this.modal) {
      this.modal.hide()
    }
  }

  searchUsers(event) {
    const searchTerm = event.currentTarget.value.trim()
    
    // Очищаем предыдущий таймаут
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (searchTerm.length === 0) {
      this.showDefaultSearchMessage()
      return
    }

    // Добавляем задержку в 500мс
    this.searchTimeout = setTimeout(() => {
      this.performSearch(searchTerm)
    }, 500)
  }

  performSearch(searchTerm) {
    const url = new URL('/admin/competitions/' + this.getCompetitionId() + '/masters/search_mdl_users', window.location.origin)
    url.searchParams.append('search', searchTerm)
    url.searchParams.append('nomination_id', this.currentNominationId)

    fetch(url, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.text())
    .then(html => {
      this.searchResultsTarget.innerHTML = html
    })
    .catch(error => {
      console.error('Search error:', error)
      this.searchResultsTarget.innerHTML = '<div class="alert alert-danger">Ошибка поиска</div>'
    })
  }

  selectUser(event) {
    const userId = event.currentTarget.dataset.userId
    const userName = event.currentTarget.dataset.userName
    
    // Обновляем интерфейс для выбранной номинации
    this.updateSelectedModel(this.currentNominationId, userId, userName)
    
    // Закрываем модальное окно
    this.closeModal()
  }

  clearModel(event) {
    const nominationId = event.currentTarget.dataset.nominationId
    this.clearModelForNomination(nominationId)
  }

  toggleNewUserForm() {
    const form = this.newUserFormTarget
    const button = this.toggleFormButtonTarget
    
    if (form.style.display === 'none' || form.style.display === '') {
      form.style.display = 'block'
      button.innerHTML = '<i class="fa-solid fa-times me-1"></i> Отменить'
      button.classList.remove('btn-outline-primary')
      button.classList.add('btn-outline-secondary')
    } else {
      form.style.display = 'none'
      button.innerHTML = '<i class="fa-solid fa-plus me-1"></i> Добавить новую модель'
      button.classList.remove('btn-outline-secondary')
      button.classList.add('btn-outline-primary')
      this.clearNewUserForm()
    }
  }

  handleFormSubmit(event) {
    console.log('handleFormSubmit called', event)
    event.preventDefault() // Предотвращаем стандартную отправку формы
    
    const form = event.target
    const formData = new FormData(form)
    
    // Блокируем кнопку отправки
    this.disableSubmitButton()
    
    // Очищаем предыдущие ошибки
    this.createUserErrorsTarget.style.display = 'none'
    this.createUserErrorsTarget.innerHTML = ''
    
    // Отправляем AJAX запрос
    fetch(form.action, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: formData
    })
    .then(response => {
      return response.json().then(data => ({
        status: response.status,
        ok: response.ok,
        data: data
      }))
    })
    .then(result => {
      console.log('Response:', result)
      
      if (result.ok && result.data.success) {
        // Успешное создание пользователя
        this.updateSelectedModel(this.currentNominationId, result.data.user.id, result.data.user.full_name)
        this.clearNewUserForm() // Очищаем форму
        this.closeModal()
        this.showNotification(result.data.message, 'success')
      } else {
        // Показываем ошибки
        if (result.data.errors) {
          this.createUserErrorsTarget.innerHTML = result.data.errors.join('<br>')
          this.createUserErrorsTarget.style.display = 'block'
        }
        // Разблокируем кнопку при ошибке
        this.enableSubmitButton()
      }
    })
    .catch(error => {
      console.error('Error:', error)
      this.createUserErrorsTarget.innerHTML = 'Произошла ошибка при создании пользователя'
      this.createUserErrorsTarget.style.display = 'block'
      // Разблокируем кнопку при ошибке
      this.enableSubmitButton()
    })
  }

  handleNewUserSuccess(event) {
    console.log('handleNewUserSuccess called', event.detail)
    const [data, status, xhr] = event.detail
    let response
    
    try {
      response = typeof xhr.response === 'string' ? JSON.parse(xhr.response) : xhr.response
    } catch (e) {
      console.error('Error parsing response:', e)
      return
    }
    
    if (response.success) {
      // Обновляем интерфейс с новым пользователем
      this.updateSelectedModel(this.currentNominationId, response.user.id, response.user.full_name)
      
      // Закрываем модальное окно
      this.closeModal()
      
      // Показываем уведомление
      this.showNotification(response.message, 'success')
    } else {
      // Показываем ошибки если они есть
      if (response.errors) {
        this.createUserErrorsTarget.innerHTML = response.errors.join('<br>')
        this.createUserErrorsTarget.style.display = 'block'
      }
    }
  }

  handleNewUserError(event) {
    console.log('handleNewUserError called', event.detail)
    const [data, status, xhr] = event.detail
    let response
    
    try {
      response = typeof xhr.response === 'string' ? JSON.parse(xhr.response) : xhr.response
    } catch (e) {
      console.error('Error parsing error response:', e)
      this.createUserErrorsTarget.innerHTML = 'Произошла ошибка при создании пользователя'
      this.createUserErrorsTarget.style.display = 'block'
      return
    }
    
    if (response.errors) {
      this.createUserErrorsTarget.innerHTML = response.errors.join('<br>')
      this.createUserErrorsTarget.style.display = 'block'
    }
  }

  // Приватные методы
  getNominationId(checkbox) {
    return checkbox.closest('[data-nomination-id]').dataset.nominationId
  }

  findModelBlock(nominationId) {
    const container = this.element.querySelector(`[data-nomination-id="${nominationId}"]`)
    return container.querySelector('[data-model-selection-target="modelBlock"]')
  }

  clearModelForNomination(nominationId) {
    const container = this.element.querySelector(`[data-nomination-id="${nominationId}"]`)
    const selectSection = container.querySelector('[data-model-selection-target="selectModelSection"]')
    const selectedSection = container.querySelector('[data-model-selection-target="selectedModelSection"]')
    const hiddenInput = container.querySelector('[data-model-selection-target="mdlUserInput"]')
    
    selectSection.style.display = 'block'
    selectedSection.style.display = 'none'
    hiddenInput.value = ''
  }

  updateSelectedModel(nominationId, userId, userName) {
    const container = this.element.querySelector(`[data-nomination-id="${nominationId}"]`)
    const selectSection = container.querySelector('[data-model-selection-target="selectModelSection"]')
    const selectedSection = container.querySelector('[data-model-selection-target="selectedModelSection"]')
    const nameElement = container.querySelector('[data-model-selection-target="selectedModelName"]')
    const hiddenInput = container.querySelector('[data-model-selection-target="mdlUserInput"]')
    
    // Делаем AJAX запрос для получения обновленного HTML с inline_code_editor
    const url = new URL('/admin/competitions/' + this.getCompetitionId() + '/masters/get_model_section', window.location.origin)
    url.searchParams.append('user_id', userId)
    url.searchParams.append('nomination_id', nominationId)
    
    fetch(url, {
      headers: {
        'Accept': 'text/html, application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      const contentType = response.headers.get('content-type')
      
      // Если получили JSON (ошибка), обрабатываем её
      if (contentType && contentType.includes('application/json')) {
        return response.json().then(data => {
          if (!data.success && data.error) {
            // Показываем уведомление об ошибке
            this.showNotification(data.error, 'error')
          }
          // Не обновляем интерфейс при ошибке
          return null
        })
      }
      
      // Если получили HTML (успех), продолжаем
      return response.text()
    })
    .then(html => {
      if (html === null) {
        // Была ошибка, не обновляем интерфейс
        return
      }
      
      // Обновляем скрытое поле
      hiddenInput.value = userId
      
      // Обновляем содержимое selectedSection новым HTML
      selectedSection.innerHTML = html
      
      // Показываем обновленную секцию
      selectSection.style.display = 'none'
      selectedSection.style.display = 'block'
    })
    .catch(error => {
      console.error('Error updating model section:', error)
      // Fallback к старому поведению в случае сетевой ошибки
      hiddenInput.value = userId
      selectSection.style.display = 'none'
      selectedSection.style.display = 'block'
      nameElement.textContent = userName
    })
  }

  resetModal() {
    this.searchInputTarget.value = ''
    this.showDefaultSearchMessage()
    this.hideNewUserForm()
    this.clearNewUserForm()
  }

  showDefaultSearchMessage() {
    this.searchResultsTarget.innerHTML = `
      <div class="text-center text-muted py-3">
        <i class="fa-solid fa-search fa-2x mb-2"></i>
        <p>Начните вводить для поиска...</p>
      </div>
    `
  }

  hideNewUserForm() {
    this.newUserFormTarget.style.display = 'none'
    this.toggleFormButtonTarget.innerHTML = '<i class="fa-solid fa-plus me-1"></i> Добавить новую модель'
    this.toggleFormButtonTarget.classList.remove('btn-outline-secondary')
    this.toggleFormButtonTarget.classList.add('btn-outline-primary')
  }

  clearNewUserForm() {
    this.createUserFormTarget.reset()
    this.createUserErrorsTarget.style.display = 'none'
    this.createUserErrorsTarget.innerHTML = ''
    
    // Удаляем превью фото, если оно есть
    const photoPreviewContainer = this.createUserFormTarget.querySelector('.photo-preview-container')
    if (photoPreviewContainer) {
      photoPreviewContainer.remove()
    }
    
    // Разблокируем кнопку отправки
    this.enableSubmitButton()
  }

  disableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      // debugger;
      const button = this.submitButtonTarget
      // Сохраняем оригинальный текст
      button.dataset.originalText = button.value;
      // Получаем локализованный текст из data-атрибута или используем fallback
      const savingText = button.dataset.savingText || 'Сохранение...'
      // Обновляем текст и блокируем кнопку
      console.log(savingText, button);
      button.value = savingText;
      button.disabled = true
    }
  }

  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      const button = this.submitButtonTarget
      // Восстанавливаем оригинальный текст
      if (button.dataset.originalText) {
        button.value = button.dataset.originalText
      }
      button.disabled = false
    }
  }

  getCompetitionId() {
    // Извлекаем ID соревнования из URL
    const pathParts = window.location.pathname.split('/')
    const competitionsIndex = pathParts.indexOf('competitions')
    return pathParts[competitionsIndex + 1]
  }

  showNotification(message, type = 'info') {
    // Простое уведомление (можно заменить на более сложное)
    let alertClass = 'alert-info'
    if (type === 'success') {
      alertClass = 'alert-success'
    } else if (type === 'error') {
      alertClass = 'alert-danger'
    }
    
    const notification = document.createElement('div')
    notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`
    notification.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;'
    notification.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    
    document.body.appendChild(notification)
    
    // Автоматическое удаление через 5 секунд
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove()
      }
    }, 15000)
  }
}
